import {
  FlashCardMetaData,
  WidgetDefinition,
} from '../../../shared/types/widget';
import GrowthStat from '../utils/GrowthStat';
import { Typography } from '../../widgets/Typography';
import { isNumber } from '../../../utilities/common/utils';
import { localeFormatNumber } from '../../../utilities/formatting';
import IndustryStandardsAlert from '../IndustryStandards/components/IndustryStandardsAlert';
import { CircleQuestionMark } from '../../../assets/icons/CircleQuestionMark';
import Tooltip from '../../widgets/Tooltip';

type FlashCardProps = {
  definition: WidgetDefinition;
  content: FlashCardMetaData;
};

function FlashCard({ definition, content }: FlashCardProps) {
  const {
    value: valueToDisplay,
    benchmark,
    target,
    growth,
    progress,
    totalUsers,
  } = content?.data?.[0] || {};
  const showBenchmarkAlert = Boolean(benchmark);

  return (
    <div className="intl-card-overflow">
      <div className="mb-3 flex flex-row items-center gap-2">
        <Typography
          variant="display-lg"
          weight="medium"
          className="flex gap-x-4"
        >
          {content?.meta?.icon && (
            <img
              src={`${import.meta.env.VITE_IMAGE_BASE_URL}${content?.meta?.icon}`}
              className="h-12 w-12"
              alt=""
            />
          )}
          {isNumber(valueToDisplay)
            ? localeFormatNumber(valueToDisplay)
            : valueToDisplay}
        </Typography>
        {(progress || totalUsers) && (
          <>
            <div className="bg-grey-500 h-1 w-1 rounded-full" />
            <div className="relative flex flex-row items-center gap-1">
              <Typography
                variant="body-md"
                weight="medium"
                className="text-grey-800"
              >
                {progress
                  ? `${progress}%`
                  : totalUsers && isNumber(totalUsers)
                    ? localeFormatNumber(totalUsers)
                    : totalUsers}
              </Typography>
              {content?.meta?.info && (
                <button>
                  <Tooltip
                    animated
                    title={
                      <Typography
                        variant="body-xs"
                        weight="medium"
                        className="text-white"
                      >
                        {content.meta.info.replace(
                          '{{progress}}',
                          progress ?? '',
                        )}
                      </Typography>
                    }
                    icon={<CircleQuestionMark className="text-grey-500" />}
                    position="top"
                    color="bg-grey-900"
                    contentClass="bg-grey-900 px-3 py-2 not-italic"
                  />
                </button>
              )}
            </div>
          </>
        )}
      </div>
      <Typography variant="body-sm" className="text-taupe-800">
        {content?.description}
      </Typography>
      {showBenchmarkAlert && (
        <IndustryStandardsAlert
          shortVersion
          showIndustryStandardEntryPoint
          isAboveBenchmark={Boolean(Number(target.isAboveBenchmark))}
          aboveBenchmarkLabel={`Above ${target.months} months industry standards`}
          belowBenchmarkLabel={`Below ${target.months} months industry standard`}
        />
      )}
      {growth && <GrowthStat score={+growth} />}
    </div>
  );
}

export default FlashCard;
